import responseActions, { getResponseStatus } from '../actions/ResponseActions';
import { sessionStatusValues } from '../services/ResponseService';
import utilService from '../services/UtilService';

export const initialState = { status: {} };

export function cloneItemResult(itemResult) {
    return utilService.deepCloneObject(itemResult);
}

export function setUserResponse(state, action) {
    const { contentRevisionId, extraResponses = [], identifier, question, response, scorableItems } = action;
    const existingItemResult = state[contentRevisionId];

    if (!existingItemResult) {
        return state;
    }

    const responsesToUpdate = { [identifier]: { identifier, response } };
    extraResponses.forEach((extraResponse) => {
        responsesToUpdate[extraResponse.identifier] = {
            identifier: extraResponse.identifier,
            response: extraResponse.response,
        };
    });

    const newItemResult = cloneItemResult(existingItemResult);
    newItemResult.sessionStatus = sessionStatusValues.PENDING_SUBMISSION;
    newItemResult.datestamp = utilService.getIsoDateStamp();
    newItemResult.numAttempts = 1;
    newItemResult.responseIdentifiers.forEach((responseIdentifier) => {
        const updatedResponse = responsesToUpdate[responseIdentifier.identifier];
        if (updatedResponse) {
            responseIdentifier.userResponse = updatedResponse.response;
        }
    });

    const responseStatus = getResponseStatus(newItemResult, question, scorableItems);
    const { allDeclarationsRepliedTo, atLeastOneInteractionRepliedTo } = responseStatus;
    const previousStatus = state.status[contentRevisionId];
    let interactionStatusHasChanged = previousStatus.atLeastOneInteractionRepliedTo !== atLeastOneInteractionRepliedTo ||
            previousStatus.allDeclarationsRepliedTo !== allDeclarationsRepliedTo;

    if (!interactionStatusHasChanged && Array.isArray(scorableItems) && Array.isArray(previousStatus.scorableItemsStatus)) {
        interactionStatusHasChanged = scorableItems.some((scorableItem, index) => {
            return scorableItem.isAttempted !== previousStatus.scorableItemsStatus[index].isAttempted;
        });
    }

    const status = !interactionStatusHasChanged
        ? state.status
        : { ...state.status, [contentRevisionId]: responseStatus };

    return Object.assign({}, state, { status }, { [contentRevisionId]: newItemResult });
}

export function storeItemResult(state, action) {
    const { contentRevisionId, itemResult, question, scorableItems } = action;
    const newItemResult = cloneItemResult(itemResult);

    const status = { ...state.status };
    status[contentRevisionId] = getResponseStatus(newItemResult, question, scorableItems);

    return { ...state, ...{ status }, [contentRevisionId]: newItemResult };
}

export function setOutcomeVariables(state, action) {
    const { contentRevisionId, outcomeVariables } = action;
    const newItemResult = cloneItemResult(state[contentRevisionId]);
    newItemResult.outcomeVariables = outcomeVariables;
    return { ...state, [contentRevisionId]: newItemResult };
}

export function resetItemResult(state, contentRevisionId) {
    const existingItemResult = state[contentRevisionId];

    if (!existingItemResult) {
        return state;
    }

    const newItemResult = cloneItemResult(existingItemResult);
    newItemResult.sessionStatus = sessionStatusValues.INITIAL;
    newItemResult.datestamp = utilService.getIsoDateStamp();
    newItemResult.responseIdentifiers = existingItemResult.responseIdentifiers.map((responseIdentifier) => {
        if (responseIdentifier.identifier === 'HINTREQUEST') {
            return responseIdentifier;
        }

        return Object.assign({}, responseIdentifier, { userResponse: [] });
    });
    if (newItemResult.outcomeVariables) {
        delete newItemResult.outcomeVariables;
    }

    const status = Object.assign({}, state.status,
        {
            [contentRevisionId]: {
                atLeastOneInteractionRepliedTo: false,
                allDeclarationsRepliedTo: false,
            },
        },
    );

    return Object.assign({}, state, { status }, { [contentRevisionId]: newItemResult });
}

export function addSecondsToDuration(state, contentRevisionId, seconds) {
    const existingItemResult = state[contentRevisionId];

    if (!existingItemResult) {
        return state;
    }

    const newDuration = existingItemResult.duration + seconds;

    const newItemResult = Object.assign({}, existingItemResult, {
        duration: newDuration,
        datestamp: utilService.getIsoDateStamp(),
    });

    return Object.assign({}, state, { [contentRevisionId]: newItemResult });
}

export function setStudentHighlights(state, contentRevisionId, highlights) {
    const existingItemResult = state[contentRevisionId];

    if (!existingItemResult) {
        return state;
    }

    const newItemResult = Object.assign({}, existingItemResult, {
        studentHighlights: highlights,
        datestamp: utilService.getIsoDateStamp(),
    });

    return Object.assign({}, state, { [contentRevisionId]: newItemResult });
}

export function setQuestionAnnotations(state, contentRevisionId, annotations) {
    const existingItemResult = state[contentRevisionId];

    if (!existingItemResult) {
        return state;
    }

    const newItemResult = Object.assign({}, existingItemResult, {
        questionAnnotations: annotations,
        datestamp: utilService.getIsoDateStamp(),
    });

    return Object.assign({}, state, { [contentRevisionId]: newItemResult });
}

export function removeResponses(state, questionIds) {
    const newState = { ...state, status: { ...state.status } };
    questionIds.forEach((questionId) => {
        delete newState[questionId];
        if (newState?.status[questionId]) {
            delete newState.status[questionId];
        }
    });

    return newState;
}

// at this point TT tries to commit the question and sees the confirmation modal box
export function setConfirmQuestionCommitTimestamp(state, questionId) {
    if (state[questionId].commitAnswerTimestamp) {
        return state;
    }

    const newResponseItem = {
        ...state[questionId],
        commitAnswerStatus: responseActions.commitTimestampStatus.CONFIRM,
    };

    return {
        ...state,
        [questionId]: newResponseItem,
    };
}

// at this point TT committed the question but the value will be stored the next StoreUSerResponse request
export function setPendingQuestionCommitTimestamp(state, questionId) {
    if (state[questionId].commitAnswerTimestamp) {
        return state;
    }

    const newResponseItem = {
        ...state[questionId],
        commitAnswerTimestamp: Date.now(),
        commitAnswerStatus: responseActions.commitTimestampStatus.PENDING,
    };

    return {
        ...state,
        [questionId]: newResponseItem,
    };
}

// at this point StoreUSerResponse request finally sent and the status can be marked as STORED
export function setStoredQuestionCommitTimestamp(state, questionId) {
    const newResponseItem = { ...state[questionId] };

    if (!newResponseItem.commitAnswerTimestamp || newResponseItem.commitAnswerStatus === responseActions.commitTimestampStatus.STORED) {
        return state;
    }

    newResponseItem.commitAnswerStatus = responseActions.commitTimestampStatus.STORED;
    return {
        ...state,
        [questionId]: newResponseItem,
    };
}

// at this point TT cancels question committing by clicking the modal box Cancel button
export function resetQuestionCommitTimestamp(state, questionId) {
    if (!state[questionId].commitAnswerTimestamp && !state[questionId].commitAnswerStatus) {
        return state;
    }

    const newResponseItem = {
        ...state[questionId],
    };

    delete newResponseItem.commitAnswerStatus;
    delete newResponseItem.commitAnswerTimestamp;

    return {
        ...state,
        [questionId]: newResponseItem,
    };
}

export function setOneWayNavigationOfflineCommitAttempt(state, isOfflineCommitAttempting) {
    return {
        ...state,
        isOfflineCommitAttempting,
    };
}

export default function responseReducer(state = initialState, action) {
    switch (action.type) {
    case responseActions.types.RESPONSE_SET_INTERACTION_RESPONSE:
        return setUserResponse(state, action);

    case responseActions.types.RESPONSE_SET_ITEM_RESULT:
        return storeItemResult(state, action);

    case responseActions.types.RESPONSE_SET_OUTCOME_VARIABLES:
        return setOutcomeVariables(state, action);

    case responseActions.types.RESPONSE_RESET_ITEM_RESULT:
        return resetItemResult(state, action.contentRevisionId);

    case responseActions.types.RESPONSE_SET_STATE:
        return action.newState;

    case responseActions.types.RESPONSE_ADD_SECONDS_TO_DURATION:
        return addSecondsToDuration(state, action.contentRevisionId, action.seconds);

    case responseActions.types.RESPONSE_SET_STUDENT_HIGHLIGHTS:
        return setStudentHighlights(state, action.contentRevisionId, action.highlights);

    case responseActions.types.RESPONSE_SET_QUESTION_ANNOTATIONS:
        return setQuestionAnnotations(state, action.contentRevisionId, action.annotations);

    case responseActions.types.RESPONSE_REMOVE_ALL_ITEM_RESULTS:
        return { ...initialState };

    case responseActions.types.RESPONSE_REMOVE_RESPONSES:
        return removeResponses(state, action.questionIds);

    case responseActions.types.RESPONSE_SET_CONFIRM_QUESTION_COMMIT_TIMESTAMP:
        return setConfirmQuestionCommitTimestamp(state, action.questionId);

    case responseActions.types.RESPONSE_SET_PENDING_QUESTION_COMMIT_TIMESTAMP:
        return setPendingQuestionCommitTimestamp(state, action.questionId);

    case responseActions.types.RESPONSE_SET_QUESTION_COMMIT_TIMESTAMP:
        return setStoredQuestionCommitTimestamp(state, action.questionId);

    case responseActions.types.RESPONSE_RESET_QUESTION_COMMIT_TIMESTAMP:
        return resetQuestionCommitTimestamp(state, action.questionId);

    case responseActions.types.RESPONSE_SET_OWN_OFFLINE_COMMIT_ATTEMPT:
        return setOneWayNavigationOfflineCommitAttempt(state, action.isOfflineCommitAttempting);

    default:
        return state;
    }
}
